import React, { useEffect, useState } from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, Popconfirm, message, Space, Tabs, Grid } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';
import {
  gelirTipleriAPI, giderTipleriAPI,
  birimlerAPI, urunTipleriAPI, malzemeTipleriAPI, cariTipleriAPI, kdvOranlariAPI,
  paketlemeTipleriAPI, faturaDurumlariAPI
} from '../services/api';

const SECENEK_KATEGORILERI = [
  { key: 'gelir',          label: 'Gelir Tipleri',     api: gelirTipleriAPI,       tipAdi: 'Gelir Tipi',    placeholder: 'Örn: Online, Gel-Al, Öğlen Servisi...' },
  { key: 'gider',          label: 'Gider Tipleri',     api: giderTipleriAPI,       tipAdi: 'Gider Tipi',    placeholder: 'Örn: Kira, Elektrik, Personel...' },
  { key: 'birim',          label: 'Birimler',          api: birimlerAPI,           tipAdi: 'Birim',         placeholder: 'Örn: kg, gr, lt, ml, adet...' },
  { key: 'urun_tipi',      label: 'Ürün Tipleri',      api: urunTipleriAPI,        tipAdi: 'Ürün Tipi',     placeholder: 'Örn: Çorba, Ana Yemek, Tatlı...' },
  { key: 'malzeme_tipi',   label: 'Malzeme Tipleri',   api: malzemeTipleriAPI,     tipAdi: 'Malzeme Tipi',  placeholder: 'Örn: Direkt Ürün, Sarf Malzeme...' },
  { key: 'cari_tipi',      label: 'Cari Tipleri',      api: cariTipleriAPI,        tipAdi: 'Cari Tipi',     placeholder: 'Örn: Tedarikçi, Müşteri, Personel...' },
  { key: 'kdv_orani',      label: 'KDV Oranları',      api: kdvOranlariAPI,        tipAdi: 'KDV Oranı',     placeholder: 'Örn: 0, 1, 10, 20', field: 'oran', inputType: 'number' },
  { key: 'paketleme_tipi', label: 'Paketleme Tipleri', api: paketlemeTipleriAPI,   tipAdi: 'Paketleme Tipi',placeholder: 'Örn: Sefer Tası, Kullan-At...' },
  { key: 'fatura_durumu',  label: 'Fatura Durumları',  api: faturaDurumlariAPI,    tipAdi: 'Fatura Durumu', placeholder: 'Örn: Fatura Kesildi, Fiş Kesildi, Beklemede...' },
];

function SecenekTablosu({ kategori }) {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { api, tipAdi, placeholder } = kategori;
  const field = kategori.field || 'ad';
  const isNumber = kategori.inputType === 'number';
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [aramaMetni, setAramaMetni] = useState('');
  const [form] = Form.useForm();

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.getAll();
      setData(res.data);
    } catch (err) {
      message.error(`${tipAdi} yüklenemedi: ` + (err.response?.data?.hata || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSubmit = async (values) => {
    try {
      if (editingRecord) {
        await api.update(editingRecord.id, values);
        message.success(`${tipAdi} güncellendi`);
      } else {
        await api.create(values);
        message.success(`${tipAdi} eklendi`);
      }
      setModalOpen(false);
      setEditingRecord(null);
      form.resetFields();
      fetchData();
    } catch (err) {
      message.error((editingRecord ? 'Güncellenemedi: ' : 'Eklenemedi: ') + (err.response?.data?.hata || err.message));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    form.setFieldsValue({ [field]: record[field] });
    setModalOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.remove(id);
      message.success(`${tipAdi} silindi`);
      fetchData();
    } catch (err) {
      message.error('Silinemedi: ' + (err.response?.data?.hata || err.message));
    }
  };

  const filteredData = aramaMetni
    ? data.filter(d => String(d[field] ?? '').toLowerCase().includes(aramaMetni.toLowerCase()))
    : data;

  const sortFn = isNumber
    ? (a, b) => Number(a[field] || 0) - Number(b[field] || 0)
    : (a, b) => (a[field] || '').localeCompare(b[field] || '', 'tr');

  const columns = [
    { title: tipAdi, dataIndex: field, key: field, sorter: sortFn },
    {
      title: 'İşlem', key: 'islem', width: isMobile ? 80 : 160,
      render: (_, record) => (
        <Space size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>{!isMobile && 'Düzenle'}</Button>
          <Popconfirm title={`Bu kaydı silmek istediğinize emin misiniz?`} onConfirm={() => handleDelete(record.id)} okText="Evet" cancelText="Hayır">
            <Button danger size="small" icon={<DeleteOutlined />}>{!isMobile && 'Sil'}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 16, flexWrap: 'nowrap' }}>
        <Space size={6} style={{ flexShrink: 0 }}>
          <Input placeholder="Ara..." prefix={<SearchOutlined />} allowClear onChange={(e) => setAramaMetni(e.target.value)} style={{ width: isMobile ? 110 : 200 }} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalOpen(true)} title={`Yeni ${tipAdi}`}>
            {isMobile ? 'Yeni' : `Yeni ${tipAdi}`}
          </Button>
        </Space>
      </div>

      <Table columns={columns} dataSource={filteredData} rowKey="id" loading={loading} pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '20', '50'], showSizeChanger: true, showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt` }} />

      <Modal title={editingRecord ? `${tipAdi} Düzenle` : `Yeni ${tipAdi}`} open={modalOpen} onCancel={() => { setModalOpen(false); setEditingRecord(null); form.resetFields(); }} onOk={() => form.submit()} width={400}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item name={field} label={tipAdi} rules={[{ required: true, message: `${tipAdi} zorunludur` }]}>
            {isNumber
              ? <InputNumber placeholder={placeholder} style={{ width: '100%' }} />
              : <Input placeholder={placeholder} />}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

function GelirTipleri() {
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  // Daha kısa etiketler — sol tab kolonu sağ tarafa fazladan yer bırakır
  const KISA_ETIKETLER = {
    gelir: 'Gelir',
    gider: 'Gider',
    birim: 'Birim',
    urun_tipi: 'Ürün Tip',
    malzeme_tipi: 'Malzeme Tip',
    cari_tipi: 'Cari Tip',
    kdv_orani: 'KDV',
    paketleme_tipi: 'Paketleme',
    fatura_durumu: 'Fatura Drm',
  };
  return (
    <div>
      <h2 style={{ margin: 0, fontSize: isMobile ? 16 : 24, marginBottom: 12 }}>Seçenek Ayarları</h2>
      <Tabs
        defaultActiveKey="gelir"
        tabPosition="left"
        items={SECENEK_KATEGORILERI.map(kat => ({
          key: kat.key,
          label: <span style={{ fontSize: 12 }}>{KISA_ETIKETLER[kat.key] || kat.label}</span>,
          children: <SecenekTablosu kategori={kat} />,
        }))}
        style={{ minHeight: 400 }}
      />
    </div>
  );
}

export default GelirTipleri;
